import React from 'react';
import useJsonLd from '../../hooks/useJsonLd';
import businessIdentity from '../../config/businessIdentity.json';

const SITE_URL = 'https://buildpilot.com.au';

const businessData = {
  name: businessIdentity.brand,
  legalName: businessIdentity.legalName,
  url: businessIdentity.url,
  logo: businessIdentity.logo,
  telephone: businessIdentity.telephone,
  email: businessIdentity.email,
  address: { '@type': 'PostalAddress', ...businessIdentity.address },
  sameAs: businessIdentity.sameAs,
};

/**
 * Mobile application schema (iOS + Android).
 *
 * Injected into the site-wide @graph so Google recognises the BuildPilot
 * app as a first-class entity attached to the Organization. Enables the
 * "app install" rich result / carousel on mobile SERPs for branded and
 * category-adjacent queries ("home builder app", "adelaide building app"
 * etc). We do NOT emit aggregateRating here because self-declared ratings
 * are policy-restricted; Google will pull live App Store / Play Store
 * ratings through the download URLs.
 */
export const MOBILE_APPLICATION = {
  '@type': 'MobileApplication',
  '@id': `${SITE_URL}/#mobile-app`,
  name: 'BuildPilot',
  applicationCategory: 'LifestyleApplication',
  operatingSystem: 'iOS, Android',
  url: `${SITE_URL}/app`,
  downloadUrl: [
    'https://apps.apple.com/au/app/buildpilot/id6477372083',
    'https://play.google.com/store/apps/details?id=com.buildpilot.android',
  ],
  installUrl: 'https://apps.apple.com/au/app/buildpilot/id6477372083',
  publisher: { '@id': `${SITE_URL}/#organization` },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'AUD',
  },
  inLanguage: 'en-AU',
  description:
    'BuildPilot is Adelaide\'s independent build companion app. Compare 30+ Adelaide partner builders, browse 760+ home designs, track your build progress, and get personalised guidance from a real human.',
};

/**
 * BuildPilot Editorial Team - the author/reviewer Organization entity
 * referenced from Article and Organization schemas. Schema.org allows
 * Organization as `author` and `reviewedBy` for Articles, so we use this
 * as the publishing-team entity.
 *
 * Credential attribution rule (important):
 *   The professional credentials (Bachelor of Architectural Studies,
 *   Master of Architecture, RLA300580) are held PERSONALLY by George
 *   Giannakakis, the founder of BuildPilot, not by the publishing
 *   organisation. The Organization-level entity therefore lists no
 *   `hasCredential` or `memberOf` arrays. Instead it points to George
 *   via `founder` and `employee`, and George (see EDITORIAL_LEAD below)
 *   carries the credentials as `Person.hasCredential`. This matches how
 *   Schema.org models qualifications - they attach to natural persons,
 *   not to the organisations they work for.
 */
export const EDITORIAL_LEAD = {
  '@type': 'Person',
  '@id': `${SITE_URL}/#founder`,
  name: 'George Giannakakis',
  givenName: 'George',
  familyName: 'Giannakakis',
  jobTitle: 'Founder and Principal, BuildPilot',
  description:
    'George Giannakakis founded BuildPilot in Adelaide after 17+ years in the South Australian building industry. He personally holds a Bachelor of Architectural Studies and a Master of Architecture from the University of South Australia, and an active South Australian real estate licence (RLA300580). He was a 2025 Australian Young Entrepreneur of the Year National Finalist and a 7NEWS Business for Good Award Finalist.',
  url: `${SITE_URL}/about/editorial-team`,
  worksFor: { '@id': `${SITE_URL}/#organization` },
  alumniOf: {
    '@type': 'CollegeOrUniversity',
    name: 'University of South Australia',
    url: 'https://www.unisa.edu.au',
    sameAs: 'https://en.wikipedia.org/wiki/University_of_South_Australia',
  },
  knowsAbout: [
    'Home Construction',
    'Architectural Design',
    'Building Contracts',
    'House and Land Packages',
    'Knockdown Rebuild',
    'Adelaide Property Market',
    'Building Costs',
    'Builder Selection',
  ],
  hasCredential: [
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'degree',
      name: 'Bachelor of Architectural Studies',
      educationalLevel: 'Undergraduate',
      recognizedBy: {
        '@type': 'CollegeOrUniversity',
        name: 'University of South Australia',
        url: 'https://www.unisa.edu.au',
        sameAs: 'https://en.wikipedia.org/wiki/University_of_South_Australia',
      },
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'degree',
      name: 'Master of Architecture',
      educationalLevel: 'Postgraduate',
      dateCreated: '2010',
      recognizedBy: {
        '@type': 'CollegeOrUniversity',
        name: 'University of South Australia',
        url: 'https://www.unisa.edu.au',
        sameAs: 'https://en.wikipedia.org/wiki/University_of_South_Australia',
      },
    },
    {
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: 'Registered Real Estate Land Agent',
      identifier: 'RLA300580',
      recognizedBy: {
        '@type': 'GovernmentOrganization',
        name: 'Consumer and Business Services South Australia',
        url: 'https://www.cbs.sa.gov.au',
        parentOrganization: { '@type': 'GovernmentOrganization', name: 'Government of South Australia' },
      },
    },
  ],
  memberOf: [
    {
      '@type': 'Organization',
      name: 'Real Estate Institute of South Australia (REISA)',
      url: 'https://reisa.com.au',
    },
  ],
  award: [
    '2025 Australian Young Entrepreneur of the Year - National Finalist (Business News Australia)',
    '2025 7NEWS Community Achievement Awards - Business for Good Award Finalist',
  ],
  sameAs: ['https://eliteagent.com/lets-work-together-george-giannakakis/'],
};

export const EDITORIAL_TEAM = {
  '@type': 'Organization',
  '@id': `${SITE_URL}/#editorial`,
  name: 'BuildPilot Editorial Team',
  url: `${SITE_URL}/about/editorial-team`,
  mainEntityOfPage: `${SITE_URL}/about/editorial-team`,
  parentOrganization: { '@id': `${SITE_URL}/#organization` },
  founder: { '@id': `${SITE_URL}/#founder` },
  employee: { '@id': `${SITE_URL}/#founder` },
  description:
    'The BuildPilot editorial team writes and reviews every guide on the site. It is led by BuildPilot founder George Giannakakis, who personally holds a Bachelor of Architectural Studies and a Master of Architecture (University of South Australia), and an active South Australian real estate licence (RLA300580). Adelaide-based, independent, 17+ years across the South Australian building industry.',
  knowsAbout: [
    'Home Construction',
    'Architectural Design',
    'Building Contracts',
    'House and Land Packages',
    'Knockdown Rebuild',
    'Adelaide Property Market',
    'Building Costs',
    'Builder Selection',
  ],
};

// Backwards-compatible alias. Older imports of FOUNDER_PERSON should
// resolve to the actual Person (George) so any author/reviewedBy field
// that previously pointed here still references a credentialled human.
export const FOUNDER_PERSON = EDITORIAL_LEAD;

/**
 * Organization schema - include on EVERY page.
 * Uses @graph to bundle Organization, WebSite, and Founder Person together.
 */
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: businessData.name,
        legalName: businessData.legalName,
        url: businessData.url,
        identifier: {
          '@type': 'PropertyValue',
          propertyID: 'ABN',
          value: businessIdentity.abn,
        },
        logo: {
          '@type': 'ImageObject',
          '@id': `${SITE_URL}/#logo`,
          url: businessData.logo,
          width: 400,
          height: 100,
          caption: 'BuildPilot',
        },
        image: businessData.logo,
        telephone: businessData.telephone,
        email: businessData.email,
        address: businessData.address,
        sameAs: businessData.sameAs,
        areaServed: businessIdentity.areaServed.map((a) => ({ '@type': a.type, name: a.name })),
        description: businessIdentity.description,
        slogan: businessIdentity.slogan,
        knowsAbout: [
          'Home Building',
          'Builder Selection',
          'Building Contracts',
          'House and Land Packages',
          'New Home Construction',
          'Knockdown Rebuild',
          'Building Costs Adelaide',
          'First Home Buyer Guidance',
        ],
        award: [
          '2025 Australian Young Entrepreneur of the Year - National Finalist',
          '2025 7NEWS Community Achievement Awards - Business for Good Award Finalist',
        ],
        publishingPrinciples: `${SITE_URL}/about/editorial-process`,
        actionableFeedbackPolicy: `${SITE_URL}/about/editorial-process`,
        ethicsPolicy: `${SITE_URL}/about/editorial-process`,
        diversityPolicy: `${SITE_URL}/about/editorial-process`,
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'BuildPilot Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Builder Comparison',
                description: 'Compare 30+ listed Adelaide builders side-by-side',
                areaServed: [
                  { '@type': 'State', name: 'South Australia' },
                  { '@type': 'State', name: 'Victoria' },
                ],
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Design Library',
                description: 'Browse 760+ Adelaide home designs and floor plans',
                areaServed: { '@type': 'Country', name: 'Australia' },
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'One on One Support',
                description: 'Premium hands-on support: builder shortlist, contract review and mid-build oversight from a real human',
                areaServed: { '@type': 'State', name: 'South Australia' },
              },
            },
          ],
        },
      },
      FOUNDER_PERSON,
      EDITORIAL_TEAM,
      MOBILE_APPLICATION,
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'BuildPilot',
        description: "Independent Australian building platform. Compare our Adelaide partner builders, browse 760+ home designs and 68 suburb guides.",
        publisher: { '@id': `${SITE_URL}/#organization` },
        copyrightHolder: { '@id': `${SITE_URL}/#organization` },
        copyrightYear: '2021',
        inLanguage: 'en-AU',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/help?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  // Inject directly into <head> via DOM (bypasses helmet sanitization
  // that has historically dropped ld+json on this site).
  useJsonLd([schema], 'organization');
  return null;
};

/**
 * WebPage schema - include on every individual page for proper indexing.
 * Phase 4: migrated from Helmet to useJsonLd (Helmet was known to silently
 * drop ld+json at production hydration time on this site).
 */
export const WebPageSchema = ({ title, description, url, dateModified, breadcrumbs }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}${url}#webpage`,
    url: `${SITE_URL}${url}`,
    name: title,
    description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
    dateModified: dateModified || '2026-03-01',
    inLanguage: 'en-AU',
    ...(breadcrumbs && {
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          ...breadcrumbs.map((b, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: b.name,
            ...(b.url ? { item: `${SITE_URL}${b.url}` } : {}),
          })),
        ],
      },
    }),
  };

  useJsonLd([schema], `webpage:${url}`);
  return null;
};

/**
 * FAQPage schema - add to any page with FAQ content.
 * Phase 4: migrated to useJsonLd.
 */
export const FAQPageSchema = ({ faqs }) => {
  const schema = (faqs && faqs.length > 0) ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  } : null;

  useJsonLd(schema ? [schema] : [], `faq:${(faqs || []).length}`);
  return null;
};

/**
 * Service schema for service-type pages.
 * Phase 4: migrated to useJsonLd.
 */
export const ServicePageSchema = ({ name, description, url }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: `${SITE_URL}${url}`,
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: { '@type': 'City', name: 'Adelaide' },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'AUD',
      availability: 'https://schema.org/InStock',
    },
  };

  useJsonLd([schema], `service:${url}`);
  return null;
};

/**
 * Article schema for blog and guide pages.
 *
 * Authorship attribution rule:
 *   `author` and `reviewedBy` point to the credentialled human
 *   (George Giannakakis, the Person node at /#founder). The publishing
 *   organisation (BuildPilot, /#organization) is the `publisher`. This
 *   is the correct Schema.org wiring: qualifications attach to natural
 *   persons, so the credential chain only lights up in SERP/AI surfaces
 *   when the credentialled Person is the named author.
 * Phase 4: migrated to useJsonLd.
 */
export const ArticlePageSchema = ({ title, description, url, datePublished, dateModified, image }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: image || `${SITE_URL}/images/buildpilot-og.jpg`,
    datePublished: datePublished || '2025-01-01',
    dateModified: dateModified || '2026-03-01',
    author: { '@id': `${SITE_URL}/#founder` },
    reviewedBy: { '@id': `${SITE_URL}/#founder` },
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${url}` },
    inLanguage: 'en-AU',
  };

  useJsonLd([schema], `article:${url}`);
  return null;
};

/**
 * BreadcrumbList schema - standalone breadcrumb component.
 * Phase 4: migrated to useJsonLd.
 */
export const BreadcrumbListSchema = ({ items }) => {
  const schema = (items && items.length > 0) ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      ...items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: item.name,
        ...(item.url ? { item: `${SITE_URL}${item.url}` } : {}),
      })),
    ],
  } : null;

  useJsonLd(schema ? [schema] : [], `breadcrumb:${(items || []).length}`);
  return null;
};

/**
 * SiteNavigationElement schema for the main navigation.
 * Phase 4: migrated to useJsonLd.
 */
export const SiteNavigationSchema = () => {
  const navItems = [
    { name: 'Building a New Home', url: '/building-a-new-home' },
    { name: 'Choosing Land', url: '/choosing-land' },
    { name: 'Knockdown Rebuild', url: '/knockdown-rebuild' },
    { name: 'Renovations', url: '/renovations' },
    { name: 'Best Builders Adelaide', url: '/home-builders-adelaide' },
    { name: 'Building Costs', url: '/building-costs-adelaide-2026' },
    { name: 'Help & Advice', url: '/help' },
    { name: 'Contact', url: '/contact' },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: 'Main Navigation',
    hasPart: navItems.map((item) => ({
      '@type': 'WebPage',
      name: item.name,
      url: `${SITE_URL}${item.url}`,
    })),
  };

  useJsonLd([schema], 'site-navigation');
  return null;
};
